<template>
  <div class="app-support-grid" :class="{ compact }">
    <ul class="list-unstyled">
      <li v-for="t in appsRef" :key="t.id" :class="{ 'icon-only': iconOnly, 'supported': isSupportedForView(t) }">
        <a :href="t.infoUrl" target="_blank" rel="noopener">
          <img :src="t.icon"/>
        </a>
        <template v-if="!iconOnly">
          <div class="app-name">{{ t.product }}</div>
          <template v-if="!compact">
            <div class="os-support">
              <img v-if="showOSIcon(t, 'windows')" src="@/assets/logos/Windows_logo_black.svg" class="windows" title="Windows support"/>
              <img v-if="showOSIcon(t, 'mac')" src="@/assets/logos/Apple_logo_grey.svg" class="mac" title="MacOS support"/>
            </div>
            <div class="app-versions">v{{ t.versions }}</div>
          </template>
        </template>
        <div class="planned-badge badge badge-xs">Soon</div>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.app-support-grid {
  display: flex;
  flex-direction: column;
  align-items: center;
}

ul {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1.5rem;
  row-gap: 4rem;
}

.app-support-grid.compact {
  align-items: stretch;
}

.app-support-grid.compact ul {
  gap: 0.5rem;
  row-gap: 1rem;
  justify-content: flex-start;
}

li {
  display: grid;
  grid-template-rows: 40px 2.4em auto auto auto;
  justify-items: center;
  align-items: start;
  row-gap: 0.25rem;
  width: 70px;

  a {
    img {
      width: 40px;
    }
  }

  .app-name {
    align-self: start;
    text-align: center;
    font-size: 0.75rem;
    color: #888;
  }

  .os-support {
    margin-top: 0.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;

    img {
      width: 12px;

      &.windows {
        margin-top: 2px;
        opacity: 0.15;
      }

      &.mac {
        opacity: 0.5;
      }
    }
  }

  .app-versions {
    margin-top: 0.5rem;
    text-align: center;
    font-size: 0.65rem;
    color: #aaa;
  }

  .planned-badge {
    margin-top: 0.5rem;
    background-color: rgb(255, 207, 119);
    padding: 0 0.5rem;
    color: #333;
  }

  &.icon-only {
    grid-template-rows: auto;
    width: auto;
  }

  &.supported {
    .planned-badge {
      display: none;
    }
  }

  &:not(.supported) {
    a img {
      filter: grayscale(1) opacity(0.4);
    }

    .app-name {
      color: #aaa;
    }
  }
}

</style>

<script setup lang="ts">
import { computed } from 'vue'
import { OS, WorkspaceTypeInfo, currentOS, workspaceTypeInfos } from '@/rd/ResearchDataModel'

const props = defineProps<{
  iconOnly?: boolean
  compact?: boolean
}>()

const appsRef = computed(() => {
  const os = currentOS
  if (props.compact && os) {
    return workspaceTypeInfos.filter(t => !t.hidden &&
      (t.supportedOn.includes(os) || t.plannedOn.includes(os)))
  }
  return workspaceTypeInfos.filter(t => !t.hidden && (t.supportedOn.length || t.plannedOn.length))
})

function isSupportedForView(t: WorkspaceTypeInfo) {
  const os = currentOS
  if (props.compact && os) {
    return t.supportedOn.includes(os)
  }
  return t.supportedOn.length > 0
}

function showOSIcon(t: WorkspaceTypeInfo, os: OS) {
  return t.supportedOn.includes(os) || (t.supportedOn.length == 0 && t.plannedOn.includes(os))
}
</script>
